import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import type {
  ChartSpec,
  InquisitorResponseData,
  MetricSpec,
  TableSpec,
} from '@/types/chat'

interface ChartRendererProps {
  data: InquisitorResponseData
}

function asMetricSpec(spec: Record<string, unknown> | null | undefined): MetricSpec | null {
  if (!spec || typeof spec.label !== 'string' || spec.value == null) {
    return null
  }
  return {
    label: spec.label,
    value: String(spec.value),
    delta: spec.delta != null ? String(spec.delta) : undefined,
    note: typeof spec.note === 'string' ? spec.note : undefined,
  }
}

function asTableSpec(spec: Record<string, unknown> | null | undefined): TableSpec | null {
  if (!spec || !Array.isArray(spec.columns) || !Array.isArray(spec.rows)) {
    return null
  }
  const columns = spec.columns.filter((column): column is string => typeof column === 'string')
  const rows = spec.rows
    .filter((row): row is unknown[] => Array.isArray(row))
    .map((row) => row.map((cell) => String(cell ?? '')))
  if (columns.length === 0) {
    return null
  }
  return {
    intro_sentence:
      typeof spec.intro_sentence === 'string' ? spec.intro_sentence : undefined,
    columns,
    rows,
  }
}

function asChartSpec(spec: Record<string, unknown> | null | undefined): ChartSpec | null {
  if (
    !spec ||
    (spec.chart_type !== 'line' && spec.chart_type !== 'bar') ||
    !Array.isArray(spec.x) ||
    !Array.isArray(spec.y)
  ) {
    return null
  }
  const x = spec.x.map((value) => String(value))
  const y = spec.y.map((value) => Number(value))
  if (x.length === 0 || x.length !== y.length) {
    return null
  }
  return {
    chart_type: spec.chart_type,
    x,
    y,
    series: typeof spec.series === 'string' ? spec.series : undefined,
    bullets: Array.isArray(spec.bullets)
      ? spec.bullets.filter((bullet): bullet is string => typeof bullet === 'string')
      : undefined,
  }
}

function MetricView({ spec }: { spec: MetricSpec }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      <div className="text-sm font-medium text-gray-600">{spec.label}</div>
      <div className="mt-1 text-3xl font-semibold tracking-tight text-gray-900">
        {spec.value}
      </div>
      {spec.delta ? (
        <div className="mt-2 text-sm text-gray-600">{spec.delta}</div>
      ) : null}
      {spec.note ? (
        <div className="mt-2 text-sm text-gray-500">{spec.note}</div>
      ) : null}
    </div>
  )
}

function TableView({ spec }: { spec: TableSpec }) {
  return (
    <div className="space-y-3">
      {spec.intro_sentence ? (
        <p className="text-sm text-gray-700">{spec.intro_sentence}</p>
      ) : null}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              {spec.columns.map((column) => (
                <th
                  key={column}
                  className="px-3 py-2 text-left font-medium text-gray-700"
                >
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {spec.rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, cellIndex) => (
                  <td key={cellIndex} className="px-3 py-2 text-gray-800">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function ChartView({ spec }: { spec: ChartSpec }) {
  const chartData = spec.x.map((label, index) => ({
    label,
    value: spec.y[index] ?? 0,
  }))
  const valueLabel = spec.series ?? 'Value'

  return (
    <div className="space-y-3">
      <div className="h-64 w-full rounded-lg border border-gray-200 bg-white p-3">
        <ResponsiveContainer width="100%" height="100%">
          {spec.chart_type === 'bar' ? (
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="value" name={valueLabel} fill="#4b5563" radius={4} />
            </BarChart>
          ) : (
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="value"
                name={valueLabel}
                stroke="#374151"
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>
      {spec.bullets && spec.bullets.length > 0 ? (
        <ul className="list-disc space-y-1 pl-5 text-sm text-gray-700">
          {spec.bullets.map((bullet) => (
            <li key={bullet}>{bullet}</li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

export default function ChartRenderer({ data }: ChartRendererProps) {
  const { response_type, message, spec } = data

  if (response_type === 'metric') {
    const metricSpec = asMetricSpec(spec)
    if (metricSpec) {
      return <MetricView spec={metricSpec} />
    }
  }

  if (response_type === 'table') {
    const tableSpec = asTableSpec(spec)
    if (tableSpec) {
      return <TableView spec={tableSpec} />
    }
  }

  if (response_type === 'chart') {
    const chartSpec = asChartSpec(spec)
    if (chartSpec) {
      return <ChartView spec={chartSpec} />
    }
  }

  return <p className="text-sm leading-relaxed text-gray-800">{message}</p>
}
